import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { QUERY_KEY_SETTINGS_ADMIN_PANEL } from '@/lib/api/constants'
import { MINUTE_IN_MS } from '@/modules/shared/constants/time'
import { createCompanyRole, editCompanyRole, getCompanyRoles, removeCompanyRole } from './api'
import { CompanyRoleInput } from './types'

export const companyRolesKey = (companyId: string) => ['company.roles', companyId]

/**
 * `fresh` - zakładki ról pobierają listę przy każdym wejściu, bo liczniki osób zmieniają się przy
 * nadawaniu ról na liście użytkowników. Plakietki i pickery korzystają z krótkiego cache.
 */
export function useQueryCompanyRoles(
  companyId: string | undefined,
  { fresh = false, enabled = true }: { fresh?: boolean; enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: companyRolesKey(companyId ?? ''),
    queryFn: () => getCompanyRoles(companyId!),
    enabled: enabled && !!companyId,
    staleTime: MINUTE_IN_MS / 2,
    refetchOnMount: fresh ? 'always' : true,
  })
}

function useInvalidateRoles(companyId: string) {
  const queryClient = useQueryClient()
  return () => {
    // Rola konta usługowego zmienia listy ról i uprawnienia także w firmach konta.
    queryClient.invalidateQueries({ queryKey: ['company.roles'] })
    queryClient.invalidateQueries({ queryKey: ['config.effectivePermissions'] })
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY_SETTINGS_ADMIN_PANEL, 'users', companyId] })
  }
}

export function useMutationSaveCompanyRole(companyId: string) {
  const invalidate = useInvalidateRoles(companyId)
  return useMutation({
    mutationFn: ({ roleId, role }: { roleId: number | null; role: CompanyRoleInput }) =>
      roleId == null ? createCompanyRole(companyId, role) : editCompanyRole(companyId, roleId, role),
    onSuccess: invalidate,
  })
}

export function useMutationRemoveCompanyRole(companyId: string) {
  const invalidate = useInvalidateRoles(companyId)
  return useMutation({
    mutationFn: (roleId: number) => removeCompanyRole(companyId, roleId),
    onSuccess: invalidate,
  })
}
